import unset from 'lodash.unset'
import set from 'lodash.set'

type BatchReplaceNestedFieldsWithIdsProps<T, TInput> = {
  object: TInput
  shouldUpdate: (key: string, value: unknown) => value is T
  newKey?: (key: string) => string
  batchUpdate: (values: T[]) => Promise<number[]>
}

/**
 * T: The type of the value to be replaced (e.g., a File object or a string)
 * TInput: The shape of the initial object
 * TResult: The expected shape of the transformed object
 */
export async function batchReplaceNestedFieldsWithIds<
  T,
  TInput extends object,
  TResult extends object
>({
  object,
  shouldUpdate,
  newKey = key => {
    // Take care that arrays still use the same index
    if (!isNaN(parseInt(key))) return key
    return `${key}Id`
  },
  batchUpdate
}: BatchReplaceNestedFieldsWithIdsProps<T, TInput>): Promise<TResult> {
  function recursive(
    obj: unknown,
    path = ''
  ): Array<{ path: string; key: string; value: T }> {
    if (!obj || typeof obj !== 'object') return []

    return Object.entries(obj).flatMap(([key, value]) => {
      const currentPath = path ? `${path}.${key}` : key

      // 1. Check if this specific field needs updating
      if (shouldUpdate(key, value)) {
        return [{ path: currentPath, key, value: value as T }]
      }

      // 2. If it's an object/array, recurse
      if (typeof value === 'object' && value !== null) {
        return recursive(value, currentPath)
      }

      return []
    })
  }

  const collectedValues = recursive(object)

  // If nothing found, return the original (cast to TResult)
  if (collectedValues.length === 0)
    return structuredClone(object) as unknown as TResult

  // Perform the async batch operation
  const ids = await batchUpdate(collectedValues.map(v => v.value))

  const newObject = removeEmpty(structuredClone(object))

  collectedValues.forEach((item, index) => {
    const id = ids[index]
    const oldPath = item.path

    // We calculate the NEW path by replacing the last segment
    // Example: "users.0.avatar" -> "users.0.avatarId"
    const pathParts = oldPath.split('.')
    pathParts[pathParts.length - 1] = newKey(item.key)
    const newPath = pathParts.join('.')

    unset(newObject, oldPath)
    set(newObject, newPath, id)
  })

  return newObject as unknown as TResult
}

/**
 * Recursively removes all keys with null or undefined values.
 */
export function removeEmpty<T>(obj: T): T {
  // 1. Base case: if it's not an object or is null/undefined, return as is
  // (We use typeof because we only want to recurse into actual objects/arrays)
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  // 2. Handle Arrays
  if (Array.isArray(obj)) {
    return (
      obj
        .map(v => removeEmpty(v))
        // Filter out both null and undefined
        .filter(v => v != null) as unknown as T
    )
  }

  // 3. Handle Objects
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const newVal = removeEmpty(value)

      // Check for both null and undefined
      if (newVal != null) {
        acc[key] = newVal
      }

      return acc
    },
    {} as Record<string, unknown>
  ) as T
}
